import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import userStore from "../../../zustand/userStore";
import { logout } from "../../../auth/authSlice";
import DemoContentButton from "../../../components/DemoContentButton";

const TimeTabeleSidebar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = userStore((state) => state.user);
  const isUserRole = userData?.roles.toLowerCase() === "user";
  const navItems = [
    { path: "/dashboard", icon: "fa-home", label: "Home" },
    { path: "/timetable/subjects", icon: "fa-book", label: "Subjects" },
    { path: "/timetable/teachers", icon: "fa-user", label: "Teachers" },
    { path: "/timetable/classes", icon: "fa-graduation-cap", label: "Classes" },
    { path: "/timetable/sections", icon: "fa-th-large", label: "Sections" },
    { path: "/timetable/periods", icon: "fa-clock-o", label: "Periods" },
    { path: "/timetable/generatetimetable", icon: "fa-calendar", label: "Time Table" },
    { path: "/timetable/teachertimetable", icon: "fa-table", label: "Teacher Timetable" },
    { path: "/timetable/substitute", icon: "fa-exchange", label: "Substitute Teacher" },
  ];
  const visibleNavItems = isUserRole
    ? navItems.filter(({ path }) =>
      ["/dashboard", "/timetable/periods", "/timetable/generatetimetable", "/timetable/teachertimetable"].includes(path)
    )
    : navItems;

  // State for dropdowns
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const logOut = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    dispatch(logout());
    navigate("/home");
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="custom-navbar">
      <div className="custom-navbar-container">

        {/* Hamburger Menu Button - Mobile Only */}
        <button
          className="hamburger-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle navigation"
        >
          <span className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        {/* Navigation Links */}
        <div className={`nav-links-container ${isMobileMenuOpen ? 'show' : ''}`}>
          <ul className="nav-links">
            {visibleNavItems.map(({ path, icon, label }) => (
              <li key={path} className={pathname === path ? "active" : ""}>
                <Link to={path} className="nav-link" onClick={handleNavClick}>
                  <i className={`fa ${icon}`}></i>
                  <span>{label}</span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Close button for mobile menu */}
          {/* <button 
            className="mobile-menu-close"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <i className="fa fa-times"></i>
          </button> */}
        </div>

        {/* Right Side - Users, Master Data, Profile */}
        <div className="nav-right-section">
          {userData.roles !== "user" && (
            <ul className="nav-links desktop-only">
              {userData.roles !== "guest_user" && (
                <li className={["/createUser", "/userList"].includes(pathname) ? "active" : ""}>
                  <Link to="/userList" className="nav-link">
                    <i className="fa fa-users"></i>
                    <span>Users</span>
                  </Link>
                </li>
              )}
              <li className={pathname === "/createMaster" ? "active" : ""}>
                <Link to="/createMaster" className="nav-link">
                  <i className="fa fa-database"></i>
                  <span>Master Data</span>
                </Link>
              </li>
            </ul>
          )}

          <DemoContentButton moduleName="Time Table" />

          {/* User Dropdown */}
          <div className="user-dropdown" ref={dropdownRef}>
            <button
              className="user-dropdown-btn"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <i className="fa fa-user"></i>
              <span className="user-name">{userData.name}</span>
              <i className={`fa fa-caret-down dropdown-arrow ${isDropdownOpen ? 'rotate' : ''}`}></i>
            </button>

            {/* Dropdown Menu - Falls from top */}
            <div className={`dropdown-menu-wrapper ${isDropdownOpen ? 'show' : ''}`}>
              <ul className="dropdown-menu-list">
                <li>
                  <span
                    className="dropdown-item"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <i className="fa fa-user-circle"></i>
                    Profile
                  </span>
                </li>
                <li className="dropdown-divider"></li>
                <li>
                  <span className="dropdown-item logout-item" onClick={logOut}>
                    <i className="fa fa-sign-out"></i>
                    Log Out
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <style>{`
        /* Main Navbar */
        .custom-navbar {
          background: linear-gradient(to right, #7B2FF7 0%, #F40076 100%);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
        }

        .custom-navbar-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          min-height: 50px;
          max-width: 100%;
          margin: 0 auto;
        }

        /* Hamburger Button */
        .hamburger-btn {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          z-index: 1002;
        }

        .hamburger-icon {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          position: relative;
        }

        .hamburger-icon span {
          display: block;
          height: 3px;
          width: 100%;
          background-color: #ffffff;
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        .hamburger-icon.open span:nth-child(1) {
          transform: translateY(7.5px) rotate(45deg);
        }

        .hamburger-icon.open span:nth-child(2) {
          opacity: 0;
        }

        .hamburger-icon.open span:nth-child(3) {
          transform: translateY(-7.5px) rotate(-45deg);
        }

        /* Navigation Links Container */
        .nav-links-container {
          display: flex;
          align-items: center;
          flex: 1;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 4px;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-links li {
          position: relative;
        }

        .nav-links .nav-link {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 12px;
          color: #ffffff;
          text-decoration: none;
          font-size: 0.8rem;
          font-weight: 500;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .nav-links .nav-link:hover {
          background-color: rgba(255, 255, 255, 0.15);
        }

        .nav-links li.active .nav-link {
          background-color: #ffffff;
          color: #7B2FF7;
          font-weight: 600;
        }

        .nav-links .nav-link i {
          font-size: 13px;
        }

        .mobile-menu-close {
          display: none;
        }

        /* Right Section */
        .nav-right-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .desktop-only {
          display: flex;
        }

        /* User Dropdown */
        .user-dropdown {
          position: relative;
          z-index: 1001;
        }

        .user-dropdown-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background-color: #ffffff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          color: #333;
          transition: all 0.2s ease;
        }

        .user-dropdown-btn:hover {
          background-color: #f0f0f0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .user-dropdown-btn i {
          font-size: 14px;
          color: #7B2FF7;
        }

        .dropdown-arrow {
          font-size: 12px !important;
          transition: transform 0.3s ease;
          margin-left: 4px;
        }

        .dropdown-arrow.rotate {
          transform: rotate(180deg);
        }

        /* Dropdown Menu Wrapper */
        .dropdown-menu-wrapper {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background-color: #ffffff;
          border-radius: 10px;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
          min-width: 200px;
          padding: 8px 0;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-15px);
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1002;
          overflow: hidden;
        }

        .dropdown-menu-wrapper.show {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-menu-wrapper::before {
          content: '';
          position: absolute;
          top: -8px;
          right: 20px;
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid #ffffff;
        }

        .dropdown-menu-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 18px;
          color: #333;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          font-weight: 500;
          text-decoration: none;
        }

        .dropdown-item:hover {
          background-color: #f5f0ff;
          color: #7B2FF7;
        }

        .dropdown-item i {
          font-size: 16px;
          width: 20px;
          text-align: center;
          color: #7B2FF7;
        }

        .dropdown-divider {
          height: 1px;
          background-color: #eee;
          margin: 6px 0;
        }

        .logout-item:hover {
          background-color: #fff0f0;
          color: #e53935;
        }

        .logout-item:hover i {
          color: #e53935;
        }

        /* Mobile Overlay */
        .mobile-overlay {
          display: none;
        }

        /* Mobile Styles */
        @media (max-width: 991px) {
          .hamburger-btn {
            display: block;
          }

          .desktop-only {
            display: none !important;
          }

          .nav-links-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 280px;
            height: 100vh;
            background: linear-gradient(180deg, #7B2FF7 0%, #9B4DFF 100%);
            flex-direction: column;
            align-items: flex-start;
            padding: 20px 0;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            z-index: 1001;
            overflow-y: auto;
          }

          .nav-links-container.show {
            transform: translateX(0);
          }

          .mobile-menu-close {
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            top: 15px;
            right: 15px;
            width: 36px;
            height: 36px;
            background: rgba(255, 255, 255, 0.2);
            border: none;
            border-radius: 50%;
            color: #ffffff;
            font-size: 18px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .mobile-menu-close:hover {
            background: rgba(255, 255, 255, 0.3);
          }

          .nav-links {
            flex-direction: column;
            width: 100%;
            gap: 4px;
            padding: 50px 16px 20px 16px;
          }

          .nav-links li {
            width: 100%;
          }

          .nav-links .nav-link {
            width: 100%;
            padding: 14px 16px;
            border-radius: 8px;
            font-size: 0.95rem;
          }

          .nav-links li.active .nav-link {
            background-color: rgba(255, 255, 255, 0.95);
          }

          .mobile-overlay {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 1000;
            animation: fadeIn 0.2s ease;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          .user-name {
            max-width: 100px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .user-dropdown-btn {
            padding: 6px 12px;
            font-size: 0.8rem;
          }

          .dropdown-menu-wrapper {
            right: 0;
            min-width: 180px;
          }

          .dropdown-menu-wrapper::before {
            right: 16px;
          }
        }

        @media (max-width: 480px) {
          .custom-navbar-container {
            padding: 0 12px;
          }

          .user-name {
            max-width: 70px;
          }

          .user-dropdown-btn {
            padding: 6px 10px;
            gap: 6px;
          }

          .dropdown-menu-wrapper {
            right: -5px;
            min-width: 170px;
          }

          .dropdown-menu-wrapper::before {
            right: 18px;
          }

          .dropdown-item {
            padding: 11px 14px;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 360px) {
          .user-name {
            display: none;
          }

          .user-dropdown-btn {
            padding: 8px 10px;
          }

          .dropdown-menu-wrapper {
            right: -8px;
          }

          .dropdown-menu-wrapper::before {
            right: 20px;
          }
        }
      `}</style>
    </nav>
  );
};

export default TimeTabeleSidebar;
